import {
  APIResponse,
  APIBlobResponse,
  PaginatedAPIResponse,
} from "../models/NetworkResponse";
import {
  downloadCSV,
  getAPI,
  postAPI,
  putAPI,
  uploadCSVAPI,
} from "./Networking";
import { SwagstoreRequestBody } from "../models/Swagstore/SwagstoreRequestBody";
import { ThemeRequestBody } from "../models/Swagstore/ThemeRequestBody";
import { ProductMappingRequestBody } from "../models/Swagstore/ProductMappingRequestBody";
import { SwagstoreProductCategory } from "../models/Swagstore/ProductMappingModel";
import { SwagstoreFilters } from "../models/Swagstore/SwagstoreFilters";

export default class SwagStoreAPI {

  createSwagStore = async (
    storeData: SwagstoreRequestBody,
    storeId?: number | string,
  ): Promise<APIResponse> => {
    try {
      const response = await postAPI(
        `/v5/swagstore${storeId ? `?store_id=${storeId}` : ""}`,
        storeData,
      );

      return response;
    } catch (error) {
      throw error;
    }
  };

  fetchStoreAndThemeDetails = async (
    storeId: number | string,
    themeId: number,
  ): Promise<APIResponse> => {
    try {
      const response = await getAPI(
        `/v5/swagstore/details?store_id=${storeId}&theme_id=${themeId}`,
      );
      return response;
    } catch (error) {
      throw error;
    }
  };

  saveThemeDetails = async (
    themeData: Partial<ThemeRequestBody>,
  ): Promise<APIResponse> => {
    try {
      const response = await postAPI(`/v5/swagstore/theme`, themeData);
      return response;
    } catch (error) {
      throw error;
    }
  };

  getCustomerInventory = async (
    customerId: number,
  ): Promise<SwagstoreProductCategory[]> => {
    try {
      const response = await getAPI(`/v5/swagstore/inventories/${customerId}`);
      return response.result;
    } catch (error) {
      throw error;
    }
  };

  // Product Mapping API (keeping for now, may need to be updated based on backend)
  updateProductMapping = async (
    storeId: number | string,
    productMappingData: ProductMappingRequestBody,
  ): Promise<APIResponse> => {
    try {
      const response = await postAPI(
        `/v5/swagstore/${storeId}/product-mapping`,
        productMappingData,
      );
      return response;
    } catch (error) {
      throw error;
    }
  };

  getPacks = async (filters?: SwagstoreFilters): Promise<APIResponse> => {
    try {
      const queryParams = new URLSearchParams();
      // Pagination removed from backend API
      // if (filters?.page) {
      //   queryParams.append("page", filters.page.toString());
      // }
      // if (filters?.limit) {
      //   queryParams.append("limit", filters.limit.toString());
      // }
      if (filters?.inventory_ids && Array.isArray(filters.inventory_ids)) {
        filters.inventory_ids.forEach((id) => {
          queryParams.append("inventory_ids", id.toString());
        });
      }
      if (filters?.order_ids && Array.isArray(filters.order_ids)) {
        filters.order_ids.forEach((id) => {
          queryParams.append("order_ids", id.toString());
        });
      }
      if (filters?.search) {
        queryParams.append("search", filters.search);
      }
      if (filters?.sort_by) {
        queryParams.append("sort_by", filters.sort_by);
      }
      if (filters?.sort_type !== undefined && filters?.sort_type !== null) {
        const sortTypeString = filters.sort_type === 0 ? "ASC" : "DESC";
        queryParams.append("sort_type", sortTypeString);
      }

      const queryString = queryParams.toString();
      const url = `/v5/swagstore/packs/${filters?.store_id}/${filters?.customer_id
        }${queryString ? `?${queryString}` : ""}`;

      const response = await getAPI(url);
      return response;
    } catch (error) {
      throw error;
    }
  };

  getProducts = async (filters?: SwagstoreFilters): Promise<APIResponse> => {
    try {
      const queryParams = new URLSearchParams();
      if (filters?.limit != null && !Number.isNaN(Number(filters.limit))) {
        queryParams.append("limit", String(filters.limit));
      }
      if (filters?.page != null && !Number.isNaN(Number(filters.page))) {
        queryParams.append("page", String(filters.page));
      }
      if (filters?.search) {
        queryParams.append("search", filters.search);
      }

      if (
        filters?.category_ids &&
        Array.isArray(filters.category_ids) &&
        filters.category_ids.length > 0
      ) {
        const validCategoryIds = filters.category_ids
          .filter((id) => !isNaN(id) && id > 0)
          .map((id) => id.toString())
          .join(",");
        if (validCategoryIds) {
          queryParams.append("category_ids", validCategoryIds);
        }
      }

      if (filters?.sort_by) {
        queryParams.append("sort_by", filters.sort_by);
      }
      if (filters?.sort_type !== undefined && filters?.sort_type !== null) {
        const sortTypeString = filters.sort_type === 0 ? "ASC" : "DESC";
        queryParams.append("sort_type", sortTypeString);
      }

      if (
        filters?.display_on_store != null &&
        !Number.isNaN(Number(filters.display_on_store))
      ) {
        queryParams.append("display_on_store", String(filters.display_on_store));
      }
      if (filters?.mockup_status) {
        queryParams.append("mockup_status", filters.mockup_status);
      }

      const queryString = queryParams.toString();
      const url = `/v5/swagstore/products/${filters?.store_id}/${filters?.customer_id
        }${queryString ? `?${queryString}` : ""}`;

      const response = await getAPI(url);
      return response;
    } catch (error) {
      throw error;
    }
  };

  getProductMockupStatusCounts = async (
    filters?: SwagstoreFilters,
  ): Promise<APIResponse> => {
    try {
      const queryParams = new URLSearchParams();
      if (filters?.search) {
        queryParams.append("search", filters.search);
      }
      if (
        filters?.category_ids &&
        Array.isArray(filters.category_ids) &&
        filters.category_ids.length > 0
      ) {
        const validCategoryIds = filters.category_ids
          .filter((id) => !isNaN(id) && id > 0)
          .map((id) => id.toString())
          .join(",");
        if (validCategoryIds) {
          queryParams.append("category_ids", validCategoryIds);
        }
      }
      if (
        filters?.display_on_store != null &&
        !Number.isNaN(Number(filters.display_on_store))
      ) {
        queryParams.append("display_on_store", String(filters.display_on_store));
      }

      const queryString = queryParams.toString();
      const url = `/v5/swagstore/products/${filters?.store_id}/${filters?.customer_id}/counts${queryString ? `?${queryString}` : ""
        }`;
      const response = await getAPI(url);
      return response;
    } catch (error) {
      throw error;
    }
  };

  togglePacksDisplay = async (
    storeId: number | string,
    packs: Array<{
      pack_id: number;
      pack_price?: number;
      display_on_store?: boolean;
      variants?: Array<{
        variant_id: number;
        product_id: number;
        quantity_for_store: number;
      }>;
    }>,
  ): Promise<APIResponse> => {
    try {
      const body = {
        store_id: storeId,
        packs: packs,
      };

      const response = await postAPI(`/v5/swagstore/display/packs`, body);
      return response;
    } catch (error) {
      throw error;
    }
  };

  toggleProductsDisplay = async (
    storeId: number | string,
    products: Array<{
      product_id: number;
      selling_price?: number;
      display_on_store?: boolean;
      limited_stock?: boolean;
      variants?: Array<{
        variant_id: number;
        product_id: number;
        quantity_for_store: number;
      }>;
    }>,
  ): Promise<APIResponse> => {
    try {
      const body = {
        store_id: storeId,
        products: products,
      };

      const response = await postAPI(`/v5/swagstore/display/products`, body);
      return response;
    } catch (error) {
      throw error;
    }
  };

  downloadCatalogueTemplate = async (
    storeId: number | string,
  ): Promise<APIBlobResponse> => {
    try {
      const response = await downloadCSV(
        `/v5/swagstore/download/catalogue?store_id=${storeId}`,
      );

      return response;
    } catch (error) {
      throw error;
    }
  };

  uploadProductsCSV = async (
    storeId: number | string,
    file: File,
  ): Promise<APIResponse> => {
    try {
      if (!file) {
        throw new Error("No file provided for upload.");
      }
      const response = await uploadCSVAPI(
        `/v5/swagstore/upload/products?store_id=${storeId}`,
        file,
      );
      return response;
    } catch (error) {
      throw error;
    }
  };

  updateProductPrice = async (
    storeId: number | string,
    productId: number,
    sellingPrice: number,
  ): Promise<APIResponse> => {
    try {
      const response = await putAPI(`/v5/swagstore/pricing`, {
        store_id: storeId,
        type: "product",
        product_id: productId,
        selling_price: sellingPrice,
      });
      return response;
    } catch (error) {
      throw error;
    }
  };

  updatePackPrice = async (
    storeId: number | string,
    packId: number,
    packPrice: number,
  ): Promise<APIResponse> => {
    try {
      const response = await putAPI(`/v5/swagstore/pricing`, {
        store_id: storeId,
        type: "pack",
        pack_id: packId,
        pack_price: packPrice,
      });
      return response;
    } catch (error) {
      throw error;
    }
  };

  applyMarkupPercentage = async (
    storeId: number | string,
    markupPercentage: number,
  ): Promise<APIResponse> => {
    try {
      const response = await putAPI(`/v5/swagstore/pricing/markup`, {
        store_id: storeId,
        markup_percentage: markupPercentage,
      });
      return response;
    } catch (error) {
      throw error;
    }
  };

  applyDiscountPercentage = async (
    storeId: number | string,
    discountPercentage: number,
  ): Promise<APIResponse> => {
    try {
      const response = await putAPI(`/v5/swagstore/pricing/discount`, {
        store_id: storeId,
        discount_percentage: discountPercentage,
      });
      return response;
    } catch (error) {
      throw error;
    }
  };

  resetPricing = async (storeId: number | string): Promise<APIResponse> => {
    try {
      const response = await postAPI(`/v5/swagstore/pricing/reset`, {
        store_id: storeId,
      });
      return response;
    } catch (error) {
      throw error;
    }
  };

  fetchCategories = async (): Promise<SwagstoreProductCategory[]> => {
    try {
      const response = await getAPI(`/v5/swagstore/categories`);
      return response.result;
    } catch (error) {
      throw error;
    }
  };

  downloadProductsPacksCSV = async (
    storeId: number | string,
  ): Promise<APIBlobResponse> => {
    try {
      const response = await downloadCSV(
        `/v5/swagstore/download/products-packs?store_id=${storeId}`,
      );
      return response;
    } catch (error) {
      throw error;
    }
  };

  uploadProductsPacksCSV = async (
    storeId: number | string,
    file: File,
  ): Promise<APIResponse> => {
    try {
      if (!file) {
        throw new Error("No file provided for upload.");
      }
      const response = await uploadCSVAPI(
        `/v5/swagstore/upload/pricing?store_id=${storeId}`,
        file,
      );
      return response;
    } catch (error) {
      throw error;
    }
  };

  updateStoreStatus = async (
    storeId: number | string,
    isActive: boolean,
  ): Promise<APIResponse> => {
    try {
      const response = await putAPI(`/v5/swagstore/status`, {
        store_id: storeId,
        is_active: isActive,
      });
      return response;
    } catch (error) {
      throw error;
    }
  };

  getAllStores = async (
    page_no?: number,
    limit?: number,
    search_text?: string,
  ): Promise<PaginatedAPIResponse<any>> => {
    try {
      const queryParams = new URLSearchParams();
      if (page_no) {
        queryParams.append("page_no", page_no.toString());
      }
      if (limit) {
        queryParams.append("limit", limit.toString());
      }
      if (search_text && search_text.trim().length > 0) {
        queryParams.append("search_text", search_text.trim());
      }

      const queryString = queryParams.toString();
      const url = `/v5/swagstore/list-stores${queryString ? `?${queryString}` : ""}`;

      const response = await getAPI(url);
      return response.result;
    } catch (error) {
      throw error;
    }
  };

  fetchAuthenticationMethods = async (): Promise<APIResponse> => {
    try {
      const response = await getAPI(`/v5/swagstore/authentication-methods`);
      return response;
    } catch (error) {
      throw error;
    }
  };

  getProductMockupLogos = async (params: {
    store_id?: number | string | null;
    customer_id: number;
    only_displayed?: boolean;
  }): Promise<
    Array<{
      id: number;
      logo_url: string;
      display: boolean;
      sort_order: number;
      created_at: string;
      updated_at: string;
    }>
  > => {
    try {
      const response = await getAPI(`/v5/swagstore/list-logos`, {
        store_id: params.store_id ?? undefined,
        customer_id: params.customer_id,
        only_displayed: params.only_displayed ?? true,
      });
      return Array.isArray(response.result) ? response.result : [];
    } catch (error) {
      throw error;
    }
  };

  saveProductMockupLogo = async (payload: {
    store_id?: number | string | null;
    customer_id: number;
    logo_url: string;
    display?: boolean;
    sort_order?: number;
  }): Promise<APIResponse> => {
    try {
      const response = await postAPI(`/v5/swagstore/upload-logo`, payload);
      return response;
    } catch (error) {
      throw error;
    }
  };

  setProductMockupLogoActive = async (payload: {
    id: number;
    active: boolean;
    store_id?: number | string | null;
    customer_id: number;
  }): Promise<APIResponse> => {
    try {
      const response = await putAPI(
        `/v5/swagstore/update-logo-display/${payload.id}`,
        payload,
      );
      return response;
    } catch (error) {
      throw error;
    }
  };
}
